
import { Component, namespace, Vue, Watch } from 'nuxt-property-decorator'
import type { MetaInfo } from 'vue-meta'
import { Context } from '@nuxt/types'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { Modal } from 'uiv'
import { RatingStatModel } from '@alao-frontend/erp-services'
import { OnCreated, OnMounted, WithHead } from '~/core/vue.types'
import { namespace as baseInfoStore, GetterType as BaseInfoGetterType, BaseInfoState } from '~/store/modules/BaseInfo'
import RatingInput from '~/components/sections/rating/RatingInput.vue'
import ReviewsSection from '~/components/sections/rating/ReviewsSection.vue'
import ReviewsFilter from '~/components/sections/rating/ReviewsFilter.vue'
import ReviewsBox from '~/components/sections/rating/ReviewsBox.vue'
import { $api } from '~/plugins/axios'
import { Review } from '~/core/offer.types'
import SaveRouteMixin from '~/mixins/save-route.mixin'
import { AlButton } from '~/components/ui'
import { useSEOMetaComposer } from '~/composables/seo.composable'

interface AsyncComponentData {
  feedbackRatingStats: RatingStatModel[],
}

const RatingSection = () => ({
  component: import(/* webpackChunkName: "sections" */ '~/components/sections/rating/RatingSection.vue'),
  delay: 200,
  timeout: 3000,
})

const BaseInfoStore = namespace(baseInfoStore)

const defaultFormState: Record<string, any> = {
  services: [],
  city: '',
  age: null,
  gender: null,
  overall: null,
  timeToInstall: null,
  service: null,
  userExperienceScore: null,
  customerService: null,
  correctInvoices: null,
  comment: '',
}

let debounce: any = null

@Component({
  nuxtI18n: {
    paths: {
      en: '/ratings/',
      de: '/bewertungen/',
      fr: '/ratings/',
    },
  },
  components: {
    AlButton,
    RatingSection,
    RatingInput,
    ReviewsSection,
    ReviewsFilter,
    ReviewsBox,
    Modal,
  },
  mixins: [SaveRouteMixin],
})
export default class RatingsPage extends Vue implements WithHead, OnCreated, OnMounted {
  @BaseInfoStore.Getter(BaseInfoGetterType.ACTIVE_PROVIDERS)
  providersList!: BaseInfoState['providers']

  feedbackRatingStats!: RatingStatModel[]

  async asyncData (): Promise<AsyncComponentData | void> {
    const feedbackRatingStats = await $api.PublicFeedbackService.getFeedbackRatingStats()

    return {
      feedbackRatingStats,
    }
  }

  private isReviewModalOpen = false
  private activeSlide = 0
  private isRatingSent = false
  private newReviewForm = {
    services: [],
    city: '',
    age: null,
    gender: null,
    overall: null,
    timeToInstall: null,
    service: null,
    userExperienceScore: null,
    customerService: null,
    correctInvoices: null,
    comment: '',
  }

  private reviewsPage = 1
  private reviewsPerPage = 5
  private reviewsCount = 0
  private reviewsFilterForm = {
    city: '',
    ageGroup: 0,
    rating: 0,
  }

  private isReviewsFullLoading = true
  private isReviewsPartLoading = false

  protected reviewsList: Review[] = []

  readonly specificSEOProvidersList = [
    'salt',
    'wingo',
    'yallo',
    'sunrise',
    'talktalk',
  ]

  get activeProvider () {
    return this.providersList[this.activeSlide]
  }

  get selectedProviderByDefault (): string {
    return (this.$route.query.provider as string) || ''
  }

  get providerIdByDefault () {
    const provider = this.providersList.find(p => p.slug === this.selectedProviderByDefault)

    return provider?.id || -1
  }

  get reviewsPagesAvailable () {
    return Math.ceil(this.reviewsCount / this.reviewsPerPage)
  }

  head (): MetaInfo {
    return useSEOMetaComposer(
      this,
      {
        title: this.seoTitle,
        description: this.seoDescription,
        route: { name: 'ratings' },
      },
    )
  }

  get seoTitle () {
    return this.specificSEOProvidersList.includes(this.selectedProviderByDefault)
      ? this.$t(`head.meta.ratings.${this.selectedProviderByDefault}.title`).toString()
      : this.$t('head.meta.ratings.title').toString()
  }

  get seoDescription () {
    return this.specificSEOProvidersList.includes(this.selectedProviderByDefault)
      ? this.$t(`head.meta.ratings.${this.selectedProviderByDefault}.description`).toString()
      : this.$t('head.meta.ratings.description').toString()
  }

  onSlideChange (slideIdx: number) {
    this.activeSlide = slideIdx
    this.resetPage()
  }

  onAddReviewClick (slideIdx: number) {
    this.activeSlide = slideIdx
    this.isReviewModalOpen = true
  }

  async onSubmit () {
    if (!await this.$validator.validate()) {
      return
    }

    const payload = {
      provider_id: this.activeProvider.id,
      city: this.newReviewForm.city,
      age_group: this.newReviewForm.age,
      gender: this.newReviewForm.gender,
      services: this.newReviewForm.services.map((service: string) => service.toLowerCase()),
      overall_score: this.newReviewForm.overall,
      time_to_install_score: this.newReviewForm.timeToInstall,
      user_experience_score: this.newReviewForm.userExperienceScore,
      customer_service_score: this.newReviewForm.customerService,
      correct_invoices_score: this.newReviewForm.correctInvoices,
      service_score: this.newReviewForm.service,
      comment: this.newReviewForm.comment,
    }

    let token

    try {
      token = await this.$recaptcha.execute('ratings')
    } catch (error) {
      throw new Error('ratings affiliateForm error:' + error)
    }

    try {
      await $api.RatingService.create({ ...payload, token })
      this.isRatingSent = true

      setTimeout(() => {
        this.isReviewModalOpen = false
      }, 3000)

      setTimeout(() => {
        this.resetForm()
        this.isRatingSent = false

        this.$nextTick(() => {
          this.$validator.reset()
        })
      }, 4000)
    } catch (e) {
    }
  }

  onReviewsFilterCityChange (val: string) {
    this.reviewsFilterForm.city = val
    this.resetPage()
    this.debouncedFetchReviews()
  }

  onReviewsFilterAgeGroupChange (val: number) {
    this.reviewsFilterForm.ageGroup = val
    this.resetPage()
    this.debouncedFetchReviews()
  }

  onReviewsFilterRatingChange (val: number) {
    this.reviewsFilterForm.rating = val
    this.resetPage()
    this.debouncedFetchReviews()
  }

  debouncedFetchReviews () {
    if (debounce) {
      clearTimeout(debounce)
    }
    debounce = setTimeout(this.fetchReviewsList, 400)
  }

  resetPage () {
    this.reviewsPage = 1
  }

  incrementPage () {
    this.reviewsPage++
    this.fetchReviewsList()
  }

  @Watch('activeSlide')
  async fetchReviewsList () {
    let loaderDebounce: any = null

    try {
      if (this.reviewsPage === 1) {
        // Prevent flicker
        loaderDebounce = setTimeout(() => {
          this.isReviewsFullLoading = true
        }, 400)
      } else {
        this.isReviewsPartLoading = true
      }

      const { city, rating, ageGroup } = this.reviewsFilterForm
      const params = {
        provider_id: this.activeProvider.id,
        per_page: this.reviewsPerPage,
        page: this.reviewsPage,
        rating,
      } as Record<string, unknown>
      if (city) {
        params.city = city
      }
      if (ageGroup !== 0) {
        params.age_group = ageGroup
      }
      const { items: results, pagination: { total: count } } = await $api.RatingService.getReviewsList(params)
      this.reviewsList = this.reviewsPage === 1
        ? results
        : [
            ...this.reviewsList,
            ...results,
          ]
      this.reviewsCount = count
    } catch (e) {
      // TODO: Handle error
      // eslint-disable-next-line no-console
      console.error(e)
    } finally {
      if (loaderDebounce) {
        clearTimeout(loaderDebounce)
      }

      this.isReviewsFullLoading = false
      this.isReviewsPartLoading = false
    }
  }

  resetForm () {
    Object.keys(this.newReviewForm).forEach(key => Vue.set(this.newReviewForm, key, defaultFormState[key]))
  }

  created () {
    this.$validator.extend('atLeastOneService', {
      getMessage: () => this.$t('errors.required') as string,
      validate: () => {
        return this.newReviewForm.services.length > 0
      },
    })
  }

  async mounted () {
    this.$analytics.GTM.sendPageViewEvent({
      routeName: this.$route.name as string,
      pageTitle: this.$metaInfo.title || '',
      fullPath: this.$route.fullPath,
      fromPath: this.$nuxt.context.from ? this.$nuxt.context.from.fullPath : '',
      contentGroup: 'Platform',
    })

    this.fetchReviewsList()

    try {
      await this.$recaptcha.init()
    } catch (e) {
      console.error(e)
    }
  }

  beforeDestroy () {
    this.$recaptcha.destroy()
  }
}
